import { Cloud } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useSyncDataSource } from '~/api/data-sources';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { useConfirmDialog } from '~/hooks/dialogs';
import { usePaginationParams } from '~/hooks/pagination';
import type { DataSourceLayout__DataSource as DataSource } from '../../queries/layout.generated';

interface SyncButtonProps {
  dataSource: DataSource;
}

export const SyncButton = ({ dataSource }: SyncButtonProps) => {
  const { t } = useTranslation();
  const [syncDataSource, { loading }] = useSyncDataSource();
  const { page, perPage } = usePaginationParams();
  const [searchParams] = useSearchParams();
  const search = searchParams.get('search') ?? '';

  const [syncConfirm, syncConfirmDialogProps] = useConfirmDialog();

  const onSync = useCallback(async () => {
    if (!(await syncConfirm())) return;
    await syncDataSource({
      variables: { input: { id: dataSource.id }, page, perPage, search },
    });
  }, [dataSource.id, page, perPage, search, syncConfirm, syncDataSource]);

  return (
    <>
      <Button
        color="primary"
        disabled={loading || !dataSource.canSync.value}
        onClick={() => {
          void onSync();
        }}
        startIcon={<Cloud />}
        variant="contained"
      >
        {t('pages.data_sources.buttons.sync')}
      </Button>
      <ConfirmDialog
        {...syncConfirmDialogProps}
        title={t('pages.data_sources.sync_dialog.title')}
        prompt={t('pages.data_sources.sync_dialog.prompt')}
        confirm={t('pages.data_sources.sync_dialog.confirm')}
      />
    </>
  );
};
